import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../api';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Key } from 'react-feather';

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // --- LOGIC HANDLERS (Igual que antes) ---
  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message);
        setTimeout(() => { setSuccess(''); setStep(2); }, 1500);
      } else {
        setError(data.detail || 'Error al enviar el código');
      }
    } catch (err) { setError('Error de conexión'); } 
    finally { setLoading(false); }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/verify-reset-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (res.ok) { setStep(3); } 
      else { setError(data.detail || 'Código inválido'); }
    } catch (err) { setError('Error de conexión'); } 
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    if (newPassword.length < 4) { setError('La contraseña debe tener al menos 4 caracteres'); return; }
    
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Contraseña actualizada exitosamente');
        setTimeout(() => navigate('/login'), 2000);
      } else { setError(data.detail || 'Error al actualizar la contraseña'); }
    } catch (err) { setError('Error de conexión'); } 
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#050505] text-white font-sans overflow-hidden">
      
      {/* --- LEFT SIDE: FORM --- */}
      <div className="w-full md:w-[50%] lg:w-[45%] flex flex-col justify-center px-8 sm:px-12 md:px-20 lg:px-24 z-10 relative">
        
        {/* Back Button */}
        <div className="absolute top-10 left-8 md:left-20">
            <Link to="/login" className="flex items-center text-gray-400 hover:text-white transition-colors gap-2 text-sm group">
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform"/> Volver al login
            </Link>
        </div>

        <div className="max-w-md w-full mx-auto mt-10">
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-3 text-white">
              {step === 1 && 'Recuperar acceso'}
              {step === 2 && 'Código de seguridad'}
              {step === 3 && 'Nueva contraseña'}
            </h1>
            <p className="text-gray-400 text-base font-light">
              {step === 1 && 'Ingresa tu email para buscar tu cuenta.'}
              {step === 2 && `Hemos enviado un código a ${email}`}
              {step === 3 && 'Asegúrate de que sea una contraseña segura.'}
            </p>
          </div>

          {/* Feedback Messages */}
          {success && (
            <div className="mb-6 p-3 bg-green-500/10 border border-green-500/20 text-green-400 text-sm rounded-lg text-center animate-fade-in">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg text-center animate-pulse">
              {error}
            </div>
          )}

          {/* STEP 1: EMAIL */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-6">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all"
                    placeholder="ejemplo@correo.com"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 mt-2 bg-white text-black font-semibold rounded-full hover:scale-[1.01] active:scale-[0.98] transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Buscando...' : 'Enviar Código'}
              </button>
            </form>
          )}

          {/* STEP 2: CODE */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-6">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Código de 6 dígitos</label>
                <div className="relative">
                    <Key className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                    <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all tracking-[0.2em] text-lg font-medium"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-4 mt-2 bg-white text-black font-semibold rounded-full hover:scale-[1.01] active:scale-[0.98] transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-center text-sm text-gray-500 hover:text-white transition-colors">
                ¿Email incorrecto? Cambiar email
              </button>
            </form>
          )}

          {/* STEP 3: NEW PASSWORD */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Nueva Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Confirmar Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 rounded-xl bg-white/[0.03] border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading || newPassword !== confirmPassword || newPassword.length < 4}
                className="w-full py-4 mt-2 bg-white text-black font-semibold rounded-full hover:scale-[1.01] active:scale-[0.98] transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Guardando...' : 'Restablecer Acceso'}
              </button>
            </form>
          )}

        </div>
      </div>

      {/* --- RIGHT SIDE: ANIMATED SPHERE (Optimized) --- */}
      <div className="hidden md:block w-[50%] lg:w-[55%] relative bg-black overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
             <img 
              src="/gif/spherefig.gif" 
              alt="Artistic Sphere" 
              className="w-full h-full object-cover will-change-transform opacity-90" 
            />
        </div>
      </div>

    </div>
  );
}